import {dynamic} from '@github-ui/dynamic'
import type {ValidationResult} from '@github-ui/entity-validators'
import {noop} from '@github-ui/noop'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import type React from 'react'
import {Suspense, useCallback, useState} from 'react'

import {VALUES} from '../../constants/values'
import type {OptimisticConnection} from '../../mutations/comment-connection-overlay'
import {useUpdateIssueCommentBodyMutation} from '../../mutations/use-comment-mutations'
import {CommentLoading} from '../CommentLoading'
import type {IssueCommentNode} from './issue-viewer-view-query-types'
import {IssueCommentViewer} from './IssueCommentViewer'

const LazyIssueCommentEditor = dynamic(
  async () => {
    const m = await import('./IssueCommentEditor')
    return {default: m.IssueCommentEditor}
  },
  {displayName: 'IssueCommentEditor'},
)

type IssueCommentAuthor = NonNullable<IssueCommentNode['author']>
type IssueCommentIssue = NonNullable<IssueCommentNode['issue']>
type IssueCommentIssueAuthor = NonNullable<IssueCommentIssue['author']>
type IssueCommentViaApp = NonNullable<IssueCommentNode['viaApp']>
type IssueCommentSponsorship = NonNullable<IssueCommentNode['authorToRepoOwnerSponsorship']>
type IssueCommentLastEdit = NonNullable<IssueCommentNode['lastUserContentEdit']>
type IssueCommentEditor = NonNullable<IssueCommentLastEdit['editor']>
type IssueCommentPinnedBy = NonNullable<IssueCommentNode['pinnedBy']>
type IssueCommentRepository = NonNullable<IssueCommentNode['repository']>

// RELAY-CLEANUP: Remove RelayCompat when the timeline pagination fragments leave Relay.
export type IssueCommentFragment = RelayCompat<
  Pick<
    IssueCommentNode,
    | 'id'
    | 'databaseId'
    | 'url'
    | 'createdAt'
    | 'authorAssociation'
    | 'viewerCanDelete'
    | 'viewerCanMinimize'
    | 'viewerCanUnminimize'
    | 'viewerCanReport'
    | 'viewerCanReportToMaintainer'
    | 'viewerCanBlockFromOrg'
    | 'viewerCanUnblockFromOrg'
    | 'viewerCanPin'
    | 'viewerCanUnpin'
    | 'minimizedReason'
    | 'showSpammyBadge'
    | 'createdViaEmail'
    | 'viewerDidAuthor'
    | 'viewerCanReadUserContentEdits'
    | 'lastEditedAt'
    | 'body'
    | 'bodyHTML'
    | 'bodyVersion'
    | 'isHidden'
    | 'viewerCanUpdate'
    | 'reactionGroups'
  > & {
    readonly author:
      | (Pick<IssueCommentAuthor, 'id' | 'avatarUrl' | 'login' | 'profileUrl' | '__typename'> & {
          readonly name?: string | null
        })
      | null
    readonly issue: Pick<IssueCommentIssue, 'id' | 'number' | 'locked' | 'databaseId'> & {
      readonly author: Pick<IssueCommentIssueAuthor, 'login'> | null
    }
    readonly viaApp: Pick<IssueCommentViaApp, 'name' | 'url'> | null
    readonly authorToRepoOwnerSponsorship: Pick<IssueCommentSponsorship, 'createdAt' | 'isActive'> | null
    readonly lastUserContentEdit: {
      readonly editor: Pick<IssueCommentEditor, 'url' | 'login'> | null
    } | null
    readonly pinnedBy: Pick<IssueCommentPinnedBy, 'login'> | null
    readonly repository: Pick<
      IssueCommentRepository,
      'id' | 'name' | 'isPrivate' | 'viewerCanCreateIssues' | 'slashCommandsEnabled' | 'nameWithOwner' | 'databaseId'
    > & {
      readonly owner: {readonly id: string; readonly login: string; readonly url: string}
    }
    // pendingMinimizeReason is a Relay client-schema field for optimistic updates; it is not in the
    // SPQ types but flows from still-Relay parents.
    readonly pendingMinimizeReason?: string | null
  }
>

export type IssueCommentProps = {
  anchorBaseUrl?: string
  comment: IssueCommentFragment
  onLinkClick?: (event: MouseEvent) => void
  onChange?: () => void
  onEditCancel?: () => void
  onReply?: (quotedComment: string) => void
  onSave?: () => void
  refAttribute?: React.RefObject<HTMLDivElement | null>
  highlightedCommentId?: string
  optimisticConnection?: OptimisticConnection
  navigate: (url: string) => void
  commentSubjectAuthorLogin?: string
  issueUpdatesChannel?: string | null
}

export function IssueComment({
  anchorBaseUrl,
  comment: data,
  navigate,
  onLinkClick,
  onChange = noop,
  onEditCancel = noop,
  onReply = noop,
  onSave = noop,
  refAttribute,
  highlightedCommentId,
  optimisticConnection,
  commentSubjectAuthorLogin,
  issueUpdatesChannel,
}: IssueCommentProps) {
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [hasDisabledActions, setHasDisabledActions] = useState(false)
  const [manualValidationResult, setManualValidationResult] = useState<ValidationResult | undefined>(undefined)
  const updateIssueComment = useUpdateIssueCommentBodyMutation()

  const saveIssueCommentBody = useCallback(
    (body: string, onCompleted: () => void, onError: () => void) => {
      if (data.databaseId == null) {
        onError()
        return
      }

      updateIssueComment({
        route: {
          owner: data.repository.owner.login,
          repo: data.repository.name,
          number: data.issue.number,
          databaseId: data.databaseId,
        },
        nodeId: data.id,
        body,
        bodyVersion: data.bodyVersion,
        onCompleted,
        onError: () => onError(),
      })
    },
    [
      data.bodyVersion,
      data.databaseId,
      data.id,
      data.issue.number,
      data.repository.name,
      data.repository.owner.login,
      updateIssueComment,
    ],
  )

  const [presavedComment, setPresavedComment, resetPresavedComment] = useSessionStorage<string>(
    VALUES.localStorageKeys.issueComment(
      'issuecomment',
      data.issue?.id ?? `fallback-${data.id ?? 'unknown'}`,
      data.id ?? '',
    ),
    '',
  )

  const commentSaved = useCallback(() => {
    setIsEditing(false)
    onSave()
    resetPresavedComment()
  }, [onSave, resetPresavedComment])

  const onCancel = () => {
    setIsEditing(false)
    resetPresavedComment()
    onEditCancel()
  }

  const onCommentChange = useCallback(
    (value: string) => {
      setPresavedComment(value)
      onChange()
    },
    [onChange, setPresavedComment],
  )

  return (
    <>
      {isEditing || isSubmitting || manualValidationResult ? (
        <Suspense fallback={<CommentLoading />}>
          <LazyIssueCommentEditor
            initialValidationResult={manualValidationResult}
            setManualValidationResult={setManualValidationResult}
            setIsEditing={setIsEditing}
            setIsSubmitting={setIsSubmitting}
            setHasDisabledActions={setHasDisabledActions}
            isSubmitting={isSubmitting}
            cancelEdit={onCancel}
            onSave={commentSaved}
            comment={data}
            repo={data}
            initialValue={presavedComment}
            onChange={onCommentChange}
            updateIssueComment={updateIssueComment}
          />
        </Suspense>
      ) : (
        <IssueCommentViewer
          anchorBaseUrl={anchorBaseUrl}
          comment={data}
          reactable={data}
          setIsEditing={() => setIsEditing(true)}
          onReply={onReply}
          onLinkClick={onLinkClick}
          refAttribute={refAttribute}
          navigate={navigate}
          highlightedCommentId={highlightedCommentId}
          optimisticConnection={optimisticConnection}
          commentSubjectAuthorLogin={commentSubjectAuthorLogin}
          createdViaEmail={data.createdViaEmail}
          hasDisabledActions={hasDisabledActions}
          onEditHover={LazyIssueCommentEditor.preload}
          onSaveBody={saveIssueCommentBody}
          issueUpdatesChannel={issueUpdatesChannel}
        />
      )}
    </>
  )
}
